/** @jsxImportSource @emotion/react */

import { memo } from "react";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import { SerializedStyles } from "@emotion/react";
import { Paper, Typography, Chip, Checkbox } from "@material-ui/core";
import { isEmpty } from "lodash";

import { Button } from "..";
import usePopupPolicy from "../../hooks/usePopupPolicy";
import { Props as ButtonProps } from "../Button";

import {
  paper,
  chip,
  titleBlock,
  priceBlock,
  percent,
  currency,
  descriptionBlock,
  descriptionWithoutBtn,
  btn,
  detailLabelBlock,
  detailLabel,
  detailText,
  checkbox,
  checkboxCheckedIcon,
  detail,
  policy,
  btnChangePlan,
} from "./styles";

interface Detail {
  label: string | React.ReactNode;
  texts?: Array<string | React.ReactNode>;
}

interface Action extends ButtonProps {
  label?: string;
}

interface Props {
  title: string;
  price: string;
  priceStyle?: SerializedStyles;
  currencyDesc?: React.ReactNode;
  description: React.ReactNode;
  button?: Action;
  details: Array<Detail>;
  isPopular?: boolean;
  discount?: string;
  showPolicy?: boolean;
  showChangePlan?: boolean;
}

const PricingPlan = ({
  title,
  price,
  priceStyle,
  currencyDesc,
  description,
  button = {},
  details,
  isPopular = false,
  discount,
  showPolicy = false,
  showChangePlan = false,
}: Props): JSX.Element => {
  const intl = useIntl();
  const { push } = useHistory();
  const popupPolicyModal = usePopupPolicy();
  const { label: btnLabel, ...otherBtnProps } = button;

  return (
    <Paper css={paper}>
      {isPopular && <Chip css={chip} label={intl.formatMessage({ id: "upsell.most-popular" })} />}
      <Typography variant="h5" css={titleBlock}>
        {title}
      </Typography>
      <Typography component="div" color="primary" css={[priceBlock, priceStyle]}>
        {price}
        {discount && (
          <Typography component="span" color="error" css={percent}>
            {discount}
          </Typography>
        )}
        {currencyDesc && (
          <Typography variant="body2" color="primary" css={currency}>
            {currencyDesc}
          </Typography>
        )}
      </Typography>

      <Typography css={[descriptionBlock, button ?? descriptionWithoutBtn]}>{description}</Typography>
      {!isEmpty(button) && (
        <Button disableElevation variant="contained" color="primary" css={btn} {...otherBtnProps}>
          {btnLabel}
        </Button>
      )}
      <ul css={detail}>
        {details.map(({ label, texts }, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={`detail-${idx}`}>
            <div css={detailLabelBlock}>
              <Checkbox css={checkbox} disableRipple checked checkedIcon={<span css={checkboxCheckedIcon} />} />
              <Typography component="span" css={detailLabel}>
                {label}
              </Typography>
            </div>
            {texts?.map((text, txtIdx) => (
              // eslint-disable-next-line react/no-array-index-key
              <p key={`text-${txtIdx}`} css={detailText}>
                {text}
              </p>
            ))}
          </li>
        ))}
      </ul>
      {showPolicy && (
        <Button color="primary" css={policy} onClick={() => popupPolicyModal()}>
          {intl.formatMessage({
            id: "popout-context.more-detail.over-charge-policy",
          })}
        </Button>
      )}
      {showChangePlan && (
        <Button css={btnChangePlan} variant="outlined" color="primary" onClick={() => push("/pricing")}>
          {intl.formatMessage({ id: "btn.change-plan" })}
        </Button>
      )}
    </Paper>
  );
};

export default memo(PricingPlan);
